import '../../common/EsCommon.css';
// For accountWarning. The file's own header calls itself the password-reset
// flow's stylesheet; it is the amber "nothing went wrong, but nothing is coming
// either" block that is wanted here, and a second copy of it under another name
// would be two opinions about one colour.
import './EsAccount.css';

import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import {API_LOGIN, API_LOGIN_DESTINATION, VIEW_BIRTHDATE, VIEW_FORGOT, VIEW_REGISTER, VIEW_WELCOME} from '../../common/EsConstants';
import { esHardNavigate, googleAuthUrl } from "../../common/EsEnv";
import { esFirstTouchSend } from "../../common/EsFirstTouch";

import { esFetch, esPost, esPrimedCsrfToken } from "../../common/EsFetch";
import { esLoginDestination } from "./EsLoginDestination";
import { useScrollIntoViewWhen } from "../../common/EsScrollIntoView";
import { esOauthMessage } from "./EsOauthMessage";
import { useEsStrings } from '../../common/EsStrings';
import EsMinis from '../../common/EsMinis';

/**
 * A refusal that is not a failure, keyed on esback's own code.
 *
 * <p>THE SERVER SENDS A CODE AND THIS FILE SENDS A SENTENCE -- the split
 * EsOauthMessage argues at length and EsForgot follows one screen later. This
 * is the same fact as EsForgot's `google` outcome and deliberately opens with
 * the same clause, so somebody who has seen one recognises the other. What
 * differs is the remedy, because it depends on the page: the forgot screen
 * sends them to the login page, and the login page has the button on it.
 *
 * <p>It NAMES NO ADDRESS. The reader typed it two fields above, and this is the
 * one sentence on the screen worth screenshotting -- it says that address is a
 * Google account.
 *
 * <p>An unrecognised code falls through to esback's own `msg` rather than to
 * nothing, so an outcome added on the server reads as a sentence instead of as
 * a form that did nothing.
 */
const loginOutcome = ( t ) => ( {
    google:
        t( 'account-login-google-account', 'That account signs in with Google, so there is no Escapade password to enter. Use the "Login with Google" button above.' ),
    // esback's XLoginChallenge: the password was right and the pool wants a new one, which
    // the reset link gives, or wants a step this form has none of.
    reset:
        t( 'account-login-reset', 'This account needs a new password before it can sign in. {reset}', { reset:
            <Link to={VIEW_FORGOT} data-testid="es-login-reset-link" className="whitespace-nowrap text-accent hover:underline">
                { t( 'account-login-reset-link', 'Reset your password' ) }
            </Link> } ),
    challenge:
        t( 'account-login-challenge', 'This sign-in can’t finish here. Please contact support with the contact form on the home page.' ),
} );

// esback's ENUM_LOGIN_BIRTHDATE: the password was right and the account has no birthdate, so
// the login waits on the birthdate step (EsBirthdate) and no session has started yet.
const LOGIN_BIRTHDATE = 'birthdate';

export default function EsLogin() {
    const t = useEsStrings();
    const [searchParams] = useSearchParams();
    // Declared up here because the register link below carries its state; the
    // note on why it is useLocation() and not the global is further down.
    const location = useLocation();

    // Seeded from the query string, not set in an effect.
    //
    // A federated login that fails comes back here as a REDIRECT carrying
    // ?oauthFailed=<code> -- see EsLoginFailureHandler -- so the reason is known
    // before the first render and there is nothing to wait for. A lazy
    // initialiser rather than a plain value because esOauthMessage reads the
    // params, and this way it runs once instead of on every keystroke in the
    // form below.
    //
    // It is ordinary error state from here on: typing and pressing Sign in
    // replaces it, which is right -- the old reason is about the attempt before
    // this one.
    const [error, setError] = useState( () => esOauthMessage( searchParams, t ) );

    // A refusal that is NOT a failure, kept apart from the one above because it
    // is drawn differently and for a reason: nothing has gone wrong, the account
    // is fine, and painting it red sends somebody looking for what they mistyped.
    // EsForgot draws the identical fact the identical way.
    // Kept as { code, text } and not as a sentence, for EsForgot's reason: the
    // code is what esback said and the wording is this file's, so a test can
    // tell a mapping that fell through from a sentence that was merely reworded.
    // A session that ran out arrives with expired on its state (EsSessionExpiry), and is told so
    // here: not a failure either.
    const [warning, setWarning] = useState( () => location.state?.expired
        ? { code: 'expired', text: t( 'account-login-expired', 'Your session has timed out, please login again.' ) }
        : null );

    // The refusal is at the TOP of a form long enough that the top is off
    // screen by the time the button is pressed, so the page answered and the
    // reader saw a button that did nothing. See EsScrollIntoView.
    const errorRef = useScrollIntoViewWhen( Boolean( error || warning ) );

    const formStatus = error ? (
        <p ref={errorRef} data-testid="es-login-error" className="formError"> {error} </p>
    ) : warning ? (
        <p ref={errorRef} data-testid="es-login-warning" data-outcome={warning.code}
           className="accountWarning"> {warning.text} </p>
    ) : (
        <p data-testid="es-login-register-prompt" className="text-center text-base font-medium text-body">
            { t( 'account-login-register-line', 'Don’t have an account? {register}', { register:
                <Link to={VIEW_REGISTER} state={ location.state } data-testid="es-login-register-link" className="whitespace-nowrap text-accent hover:underline">
                    { t( 'account-login-register', 'Register' ) }
                </Link> } ) }
        </p>
    );

    const blankForm = {
        email: '',
        password: '',
        keepSignedIn: 'off',
    };

    const navigate = useNavigate();
    const [form, setForm] = useState(blankForm);
    // VIEW_WELCOME rather than "/", which is the marketing page a logged-out
    // visitor sees -- landing there after a successful login looks exactly like
    // a login that did nothing. The welcome page sends a reader whose tier DOES
    // open something straight on to it, so this costs an alpha nothing and
    // gives a beta a page that explains itself.
    //
    // A `from` still wins: somebody who was sent to the login by RequireAuth is
    // going back where they were trying to go.
    // ⚠️ useLocation(), and it was the GLOBAL `location` until 2026-09-18.
    //
    // window.location has no `state`, so this read undefined every single time
    // and `from` was ALWAYS the welcome page. The comment above has claimed
    // since it was written that "a from still wins" -- it never did. Every
    // reader RequireAuth bounced to the login landed on the welcome page
    // instead of the page they had asked for, and nothing said so, because
    // welcome is a perfectly reasonable place to arrive.
    //
    // Found because the opt-out link's redirect kept landing there: the same
    // bug, one caller later. (Declared at the top of the component now.)

    // Tim, 2026-10-01: a login from a gated page returns to it, once. A login
    // started later from the front page carries no state and goes to welcome, and
    // a saved destination of /, login, register, or welcome is ignored -- see
    // esLoginDestination. The search string rides along: a gated page may need it.
    const from = esLoginDestination( location ) ?? VIEW_WELCOME;

    // The same destination for Login with Google, which cannot carry it through
    // the hosted UI's redirect: it is posted first, and an EMPTY one clears
    // whatever an earlier press left on the session, so only a login sent from a
    // gated page returns there. A failed post does not stop the login; it lands
    // on welcome.
    const loginWithGoogle = async () => {
        // A first Google sign-in makes the account, so it carries the first touch too.
        await esFirstTouchSend();

        try {
            await esPost( API_LOGIN_DESTINATION, { path: esLoginDestination( location ) ?? '' } );
        }
        catch {
            // see above
        }

        esHardNavigate( googleAuthUrl() );
    };


    // A checkbox's value is "on" whether it is ticked or not, so Keep me logged in posts its
    // checked state instead, or unticking it still kept the session (T99).
    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;

        setForm({ ...form, [name]: type === 'checkbox' ? ( checked ? 'on' : 'off' ) : value });
    };

    function validateForm() {
        let errorText = '';

        if (!form.email) errorText += t( 'account-login-email-required', 'Email is required.' ) + '\n';
        if (!form.password) errorText += t( 'account-login-password-required', 'Password is required.' ) + '\n';

        setError(errorText);

        return !errorText;
    }

    const handleSubmit = async (event) => {
        event.preventDefault();

        // Both cleared before the attempt, or the answer to the LAST address
        // typed sits under the form while this one is in flight -- and the two
        // states can only ever contradict each other.
        setWarning(null);

        if (!validateForm()) return;

        let aBodyJson = null;

        try {
            const aResponse = await esFetch(API_LOGIN, {
                method: 'POST',
                headers: {
                    // Primed rather than merely read, and this form is why it
                    // exists: the login is the first write a cold page makes,
                    // so the cookie has often not been minted yet. See
                    // esPrimedCsrfToken.
                    'X-XSRF-TOKEN': await esPrimedCsrfToken(),
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(form),
                credentials: 'include',
            });

            const aBody = await aResponse.text();

            // Only parse what is actually JSON. When esback throws, Spring
            // answers the whitelabel error page, and JSON.parse on HTML threw a
            // SyntaxError that landed in setError -- so the screen showed
            // React error #31 ("objects are not valid as a React child")
            // instead of anything about the login.
            if (aBody) {
                try {
                    aBodyJson = JSON.parse(aBody);
                }
                catch {
                    aBodyJson = null;
                }
            }

            // The CODE first, because it is the only part of the answer both
            // sides agreed about. esback sends a sentence beside it for callers
            // that are not this page; this page has its own, which can say "the
            // button above" because it is the page the button is on.
            if (aBodyJson?.outcome === LOGIN_BIRTHDATE) {
                setForm( blankForm );
                void navigate( VIEW_BIRTHDATE, { state: location.state } );
                return;
            }

            const aWarning = loginOutcome( t )[aBodyJson?.outcome];

            if (aWarning) setWarning({ code: aBodyJson.outcome, text: aWarning });
            else if (aBodyJson?.msg) setError(aBodyJson.msg);
            else if (!aResponse.ok) setError( t( 'account-login-server-error', 'Sorry — the server could not sign you in (error {status}).', { status: String( aResponse.status ) } ) );
        }
        catch (err) {
            // err is an Error, and an Error cannot be rendered. Its message can.
            setError(err.message || t( 'common-server-unreachable', 'Sorry — could not reach the server.' ));
            return;
        }

        // Only on a clean response. This used to read aBodyJson.msg with no
        // guard, so a failed request threw a TypeError on null right after the
        // error had been set, and the navigation below ran on a login that had
        // not happened.
        if (aBodyJson && !aBodyJson.msg) {
            setForm( blankForm );

            // ⚠️ THE RELOAD IS WHY THE PATH IS PASSED TO navigate AND NOT ONLY
            // TO THE ROUTER. A reload throws the SPA away and rebuilds it from
            // the browser's current URL, so the navigate above has to have
            // already changed that URL -- which it has, synchronously, before
            // the reload runs.
            //
            // The reload itself is what makes the whole app see the new session:
            // AuthProvider reads /api/v1/authUser once at mount, so without it
            // the nav bar, the flags and every gate stay logged out until
            // something else remounts them.
            void navigate(from, { replace: true });
            window.location.reload();
        }
    };

    return (
        <section data-testid="es-login-page" className="relative z-10 pb-4 pt-nav-room">
            <EsMinis />
            <div className="container">
                <div className="flex flex-wrap mx-[-16px]">
                    <div className="w-full px-4">
                        <div className="mx-auto rounded-md p-12 max-w-[500px] sm:p-[60px]">
                            <h3 data-testid="es-login-heading" className="mb-10 text-center text-2xl font-bold text-heading sm:text-3xl">
                                { t( 'account-login-heading', 'Login to your account' ) }
                            </h3>
                            { /* WIRED 2026-09-17. It had no onClick and no href
                                 here or on the register page -- "Login with
                                 Google" had always been decoration, and the
                                 code said so. The backend was complete the
                                 whole time: EsAuthConfig wires .oauth2Login(),
                                 EsOauthRegisterService handles the return, and
                                 the pool has had the Google provider since the
                                 Cognito stack was deployed with the secrets in
                                 place.

                                 A FULL PAGE NAVIGATION and never a fetch. This
                                 goes to Cognito's hosted UI and then to Google,
                                 which is a cross-origin redirect chain an XHR
                                 cannot follow -- and the session cookie has to
                                 travel with it, because the tier a register
                                 page stashed lives on that session.

                                 googleAuthUrl builds it from serverUrl rather
                                 than as a bare "/oauth2/..." -- see the note
                                 there.

                                 type="button" because it is outside the form
                                 below. Without it a browser treats it as the
                                 form's submit, so pressing Google would post
                                 the login form -- which it did not do only
                                 because the button did nothing at all. */ }
                            <button type="button"
                                    data-testid="es-login-google-button"
                                    onClick={ loginWithGoogle }
                                    className="w-full flex items-center justify-center p-3 bg-social-button text-body hover:text-accent text-base font-medium dark:hover:text-white rounded-md shadow-one dark:shadow-register mb-6">
                                <span className="me-3">
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <g clipPath="url(#clip0_95:967)">
                                            <path
                                                d="M20.0001 10.2216C20.0122 9.53416 19.9397 8.84776 19.7844 8.17725H10.2042V11.8883H15.8277C15.7211 12.539 15.4814 13.1618 15.1229 13.7194C14.7644 14.2769 14.2946 14.7577 13.7416 15.1327L13.722 15.257L16.7512 17.5567L16.961 17.5772C18.8883 15.8328 19.9997 13.266 19.9997 10.2216"
                                                fill="#4285F4"
                                            />
                                            <path
                                                d="M10.2042 20.0001C12.9592 20.0001 15.2721 19.1111 16.9616 17.5778L13.7416 15.1332C12.88 15.7223 11.7235 16.1334 10.2042 16.1334C8.91385 16.126 7.65863 15.7206 6.61663 14.9747C5.57464 14.2287 4.79879 13.1802 4.39915 11.9778L4.27957 11.9878L1.12973 14.3766L1.08856 14.4888C1.93689 16.1457 3.23879 17.5387 4.84869 18.512C6.45859 19.4852 8.31301 20.0005 10.2046 20.0001"
                                                fill="#34A853"
                                            />
                                            <path
                                                d="M4.39911 11.9777C4.17592 11.3411 4.06075 10.673 4.05819 9.99996C4.0623 9.32799 4.17322 8.66075 4.38696 8.02225L4.38127 7.88968L1.19282 5.4624L1.08852 5.51101C0.372885 6.90343 0.00012207 8.4408 0.00012207 9.99987C0.00012207 11.5589 0.372885 13.0963 1.08852 14.4887L4.39911 11.9777Z"
                                                fill="#FBBC05"
                                            />
                                            <path
                                                d="M10.2042 3.86663C11.6663 3.84438 13.0804 4.37803 14.1498 5.35558L17.0296 2.59996C15.1826 0.901848 12.7366 -0.0298855 10.2042 -3.6784e-05C8.3126 -0.000477834 6.45819 0.514732 4.8483 1.48798C3.2384 2.46124 1.93649 3.85416 1.08813 5.51101L4.38775 8.02225C4.79132 6.82005 5.56974 5.77231 6.61327 5.02675C7.6568 4.28118 8.91279 3.87541 10.2042 3.86663Z"
                                                fill="#EB4335"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_95:967">
                                                <rect width="20" height="20" fill="white" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </span>
                                { t( 'account-login-google', 'Login with Google' ) }
                            </button>
                            <div className="mb-8 flex items-center justify-center pt-4">
                                <span className="hidden w-full bg-section-rule max-w-[70px] h-[1px] sm:block"></span>
                                <p className="w-full px-5 text-center text-base font-medium text-body">{ t( 'account-login-or', 'Or login here' ) }</p>
                                <span className="hidden w-full bg-section-rule max-w-[70px] h-[1px] sm:block"></span>
                            </div>
                            <form id="loginForm" data-testid="es-login-form" onSubmit={handleSubmit}>
                                <div className="mb-8">
                                    <label
                                        className="mb-3 block text-sm font-medium text-heading"
                                        htmlFor="email"
                                    >
                                        { t( 'account-login-email', 'Email' ) }{' '}
                                    </label>
                                    <input autoComplete="username"
                                        onChange={handleChange}
                                        type="text"
                                        id="email"
                                        name="email"
                                        data-testid="es-login-email-input"
                                        className="w-full border border-field-edge bg-field rounded-md shadow-one dark:shadow-register py-3 px-6 text-body text-base placeholder-placeholder outline-hidden focus-visible:shadow-none focus:border-focus"
                                    />
                                </div>
                                <div className="mb-8">
                                    <label className="mb-3 block text-sm font-medium text-heading" htmlFor="password">
                                        { t( 'account-login-password', 'Password' ) }{' '}
                                    </label>
                                    <input autoComplete="current-password"
                                        onChange={handleChange}
                                        type="password"
                                        id="password"
                                        name="password"
                                        data-testid="es-login-password-input"
                                        className="w-full border border-field-edge bg-field rounded-md shadow-one dark:shadow-register py-3 px-6 text-body text-base placeholder-placeholder outline-hidden focus-visible:shadow-none focus:border-focus"
                                    />
                                </div>
                                { /* Below sm the two halves wrap rather than touch, and the
                                     checkbox takes a 24px target a thumb can find. */ }
                                <div className="mb-8 flex items-center justify-between max-sm:flex-wrap max-sm:gap-x-4 max-sm:gap-y-3">
                                    <div>
                                        <label
                                            htmlFor="keepSignedIn"
                                            className="flex cursor-pointer select-none items-center text-sm font-medium text-body">
                                            <div className="relative">
                                                <input type="checkbox" id="keepSignedIn" name="keepSignedIn" data-testid="es-login-keep-logged-in" onChange={handleChange} className="me-2 max-sm:block max-sm:size-6"/>
                                            </div>
                                            { t( 'account-login-keep', 'Keep me logged in' ) }
                                        </label>
                                    </div>
                                    <div>
                                        <a href={VIEW_FORGOT} data-testid="es-login-forgot-link" className="text-sm font-medium text-accent hover:underline">
                                            {' '}
                                            { t( 'account-login-forgot', 'Forgot Password?' ) }{' '}
                                        </a>
                                    </div>
                                </div>
                                <div className="mb-6">
                                    <button id="loginButton"
                                        data-testid="es-login-submit-button"
                                        type="submit"
                                        className="buttonSubmit flex w-full items-center justify-center">
                                        { t( 'account-login-submit', 'Login' ) }
                                    </button>
                                </div>
                            </form>
                            {formStatus}
                        </div>
                    </div>
                </div>
            </div>
            <div className="absolute top-0 inset-x-0 z-[-1] overflow-hidden">
                <svg width="1440" height="969" viewBox="0 0 1440 969" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <mask id="mask0_95:1005" maskUnits="userSpaceOnUse" x="0" y="0" width="1440" height="969">
                        <rect width="1440" height="969" fill="#090E34" />
                    </mask>
                    <g mask="url(#mask0_95:1005)">
                        <path
                            opacity="0.1"
                            d="M1086.96 297.978L632.959 554.978L935.625 535.926L1086.96 297.978Z"
                            fill="url(#paint0_linear_95:1005)"
                        />
                        <path
                            opacity="0.1"
                            d="M1324.5 755.5L1450 687V886.5L1324.5 967.5L-10 288L1324.5 755.5Z"
                            fill="url(#paint1_linear_95:1005)"
                        />
                    </g>
                    <defs>
                        <linearGradient
                            id="paint0_linear_95:1005"
                            x1="1178.4"
                            y1="151.853"
                            x2="780.959"
                            y2="453.581"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop offset="0" stopColor="#4A6CF7" />
                            <stop offset="1" stopColor="#4A6CF7" stopOpacity="0" />
                        </linearGradient>
                        <linearGradient
                            id="paint1_linear_95:1005"
                            x1="160.5"
                            y1="220"
                            x2="1099.45"
                            y2="1192.04"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop offset="0" stopColor="#4A6CF7" />
                            <stop offset="1" stopColor="#4A6CF7" stopOpacity="0" />
                        </linearGradient>
                    </defs>
                </svg>
            </div>
        </section>
    );
}
